import type { QueryClient } from '@tanstack/react-query'
import { createRouter, type RouterHistory } from '@tanstack/react-router'
import { toast } from '@volee/ui'

import { messages } from './messages/index.ts'
import { ME_KEY } from './auth/me.ts'
import { onSessionLost } from './services/api.ts'
import { signInRoute } from './routes/sign-in.tsx'
import { forbiddenRoute } from './routes/forbidden.tsx'
import { forgotPasswordRoute } from './routes/forgot-password.tsx'
import { settingsRoute } from './routes/settings.tsx'
import { homeRoute } from './routes/home.tsx'
import { rootRoute } from './routes/root.tsx'
import { clubDetailRoute } from './routes/club-detail.tsx'
import { newClubRoute } from './routes/new-club.tsx'
import { clubsRoute } from './routes/clubs.tsx'
import { pitchDetailRoute } from './routes/pitch-detail.tsx'
import { personDetailRoute } from './routes/person-detail.tsx'
import { appointmentsRoute } from './routes/appointments.tsx'
import { seasonsRoute } from './routes/seasons.tsx'
import { playersRoute } from './routes/players.tsx'
import { newPitchRoute } from './routes/new-pitch.tsx'
import { kitDetailRoute } from './routes/kit-detail.tsx'
import { kitsRoute } from './routes/kits.tsx'
import { LEGACY_KIT_ROUTES } from './routes/legacy-kit-paths.tsx'
import { pitchKitsRoute } from './routes/pitch-kits.tsx'
import { pitchesRoute } from './routes/pitches.tsx'
import { staffRoute } from './routes/staff.tsx'
import { usersRoute } from './routes/users.tsx'
import { userDetailRoute } from './routes/user-detail.tsx'
import { newStaffRoute } from './routes/new-staff.tsx'
import { registrationsRoute } from './routes/registrations.tsx'
import { registrationDetailRoute } from './routes/registration-detail.tsx'
import { LEGACY_REGISTRATION_ROUTES } from './routes/legacy-registration-paths.tsx'
import { leagueCategoriesRoute } from './routes/league-categories.tsx'
import { leagueGroupDetailRoute } from './routes/league-group-detail.tsx'
import { leaguesRoute } from './routes/leagues.tsx'
import { bulletinSourcesRoute } from './routes/bulletin-sources.tsx'
import { matchDetailRoute } from './routes/match-detail.tsx'
import { matchesRoute } from './routes/matches.tsx'
import { matchReportDetailRoute, matchReportsRoute } from './routes/match-reports.tsx'

// The route tree, declared by hand: no codegen plugin, because the tree is small and code
// splitting does not depend on one — `lazyRouteComponent` does it route by route (§6).
const routeTree = rootRoute.addChildren([
  homeRoute,
  signInRoute,
  forgotPasswordRoute,
  forbiddenRoute,
  settingsRoute,
  // The detail is a sibling: `/clubs` as its parent would become its layout. `new` is a child on
  // purpose, a Panel over the list; static segments outrank `$id`, so it wins anyway.
  clubsRoute.addChildren([newClubRoute]),
  clubDetailRoute,
  pitchesRoute.addChildren([newPitchRoute]),
  pitchKitsRoute,
  kitsRoute,
  kitDetailRoute,
  ...LEGACY_KIT_ROUTES,
  pitchDetailRoute,
  // The invitation is a Panel over the list, like the club's.
  staffRoute.addChildren([newStaffRoute]),
  // Siblings, and `/players` outranks the dynamic segment anyway.
  playersRoute,
  usersRoute,
  userDetailRoute,
  // Siblings, and the static /appointments/requests outranks the dynamic segment anyway.
  appointmentsRoute,
  registrationsRoute,
  registrationDetailRoute,
  ...LEGACY_REGISTRATION_ROUTES,
  seasonsRoute,
  personDetailRoute,
  leaguesRoute,
  leagueCategoriesRoute,
  leagueGroupDetailRoute,
  matchesRoute,
  matchDetailRoute,
  matchReportsRoute,
  matchReportDetailRoute,
  bulletinSourcesRoute,
])

// `history` is injectable for the tests: `createMemoryHistory` mounts the app on a path with
// no browser, which is what lets a page be tested against the real route tree.
export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  const router = createRouter({
    routeTree: routeTree,
    context: { queryClient },
    history,
    // With a session already cached, the guard must not flash a spinner on every
    // transition.
    defaultPreload: 'intent',
  })

  // A session can end during a call, not only on a transition. Being moved off a half-filled
  // form without a word looks like the app breaking, so the toast is not decoration.
  onSessionLost(() => {
    // Dropped, not just invalidated: the entry has a 5-minute `staleTime`, so the guard would
    // hand the next navigation a session that is already gone.
    queryClient.removeQueries({ queryKey: ME_KEY })
    // A stable id: the loss is reported once per failing request, so N concurrent calls would
    // otherwise stack N identical toasts.
    toast.error(messages.session.expired, { id: 'session-expired' })
    void router.navigate({
      to: signInRoute.to,
      search: { returnTo: router.state.location.href, reason: 'session-expired' },
    })
  })

  return router
}

export type Router = ReturnType<typeof createAppRouter>

// This is what types `Link to=`, the search params and `navigate` across the whole app: the
// reason the router is TanStack and not React Router (see the appendix).
declare module '@tanstack/react-router' {
  interface Register {
    router: Router
  }

  // What the shell draws around a route. `bare` is a page reached without a usable back office,
  // where the page owns its session control: apps/admin-panel/docs/shell-decisa-dalla-route.md
  interface StaticDataRouteOption {
    chrome?: 'app' | 'bare'
  }
}
